/**
 * The Place search's calls to Google, made from the browser as iOS makes them
 * from the device: typed text to Search results (Places Autocomplete), and a
 * destination to its Suggestions (Places Text Search). Nothing Google returns
 * is stored beyond the page's memory — Google's terms forbid keeping it.
 */

import { loadGooglePlaces } from './googleMaps'
import { GooglePlace } from './place'
import { PlaceSuggestion, SearchArea } from './placeSearch'

/**
 * An autocomplete billing session: the searches typed toward one pick bill as
 * one. Start a new one after each pick.
 */
export type SearchSession = google.maps.places.AutocompleteSessionToken

export const newSearchSession = async (): Promise<SearchSession> => {
	await loadGooglePlaces()
	return new google.maps.places.AutocompleteSessionToken()
}

const locationBiasOf = (area: SearchArea): google.maps.LatLngBoundsLiteral | google.maps.CircleLiteral =>
	area.kind === 'bounds'
		? {
				north: area.northEast.lat,
				east: area.northEast.lng,
				south: area.southWest.lat,
				west: area.southWest.lng,
			}
		: { center: area.center, radius: area.radiusMeters }

/** The Search results for typed text, leaning toward `bias` without being restricted to it. */
export const searchPlaces = async (
	input: string,
	session: SearchSession | null,
	bias: SearchArea | null,
): Promise<GooglePlace[]> => {
	await loadGooglePlaces()
	const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
		input,
		...(session ? { sessionToken: session } : {}),
		...(bias ? { locationBias: locationBiasOf(bias) } : {}),
	})
	return suggestions.flatMap(({ placePrediction: prediction }) =>
		prediction
			? [
					{
						placeId: prediction.placeId,
						primaryText: prediction.mainText?.text ?? prediction.text.text,
						secondaryText: prediction.secondaryText?.text ?? '',
						fullText: prediction.text.text,
						types: prediction.types,
					},
				]
			: [],
	)
}

/** As many Suggestions as iOS offers. */
const SUGGESTION_COUNT = 20

/**
 * A destination's Suggestions, looked up by its name ("Tokyo, Japan") — which
 * works for a country as well as a neighbourhood and needs no coordinates.
 */
export const fetchSuggestions = async (placeName: string): Promise<PlaceSuggestion[]> => {
	await loadGooglePlaces()
	const { places } = await google.maps.places.Place.searchByText({
		textQuery: `top tourist attractions in ${placeName}`,
		fields: ['id', 'displayName', 'formattedAddress', 'types'],
		maxResultCount: SUGGESTION_COUNT,
	})
	return places.flatMap(place =>
		place.id && place.displayName
			? [
					{
						placeId: place.id,
						name: place.displayName,
						address: place.formattedAddress ?? null,
						types: place.types ?? [],
					},
				]
			: [],
	)
}
